import React, { useMemo, useState, useEffect } from 'react';
import {
  Typography,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
  Grid,
  Alert,
  Button,
  Snackbar,
  Tooltip,
  Stack,
} from '@mui/material';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Table from '../../../common/components/Table/Table';
import EditModal from '../../../common/components/Table/EditModal';
import { useE53StratumData } from './hooks';
import { useE53FileDetails } from './hooks/useE53FileDetails';
import { E53StratumData } from './hooks/useE53StratumData';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '../../../hooks';
import { MainContent } from './e53Message.styled';
import { PageContainer, ContentPaper, StyledContainer } from './ViewSamples.styled';
import { ContextMenuAction } from '../../../common/components/Table/types';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';

// Import the specific hook from useE53StratumMutation.ts
import { useUpdateE53Stratum } from './hooks/useE53StratumMutation';

const ViewStratum: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navItems = useNavItems();

  // Permission checks
  const { hasPermission, isLoading: permissionLoading } = usePermission('/e53', '');
  const { hasPermission: hasEditPermission, isLoading: editPermissionLoading } = usePermission(
    '/e53/samples/edit',
    ''
  );

  // State to track the row being edited
  const [editingRowId, setEditingRowId] = useState<string | number | null>(null);

  // State for EditModal
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  // State for notification messages
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  // Track editMode separately to avoid race conditions with permissions
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  useEffect(() => {
    // Wait for permissions to load before deciding on edit mode
    if (!editPermissionLoading) {
      const editModeRequested = searchParams.get('editMode') === 'true';
      setIsEditMode(editModeRequested && hasEditPermission);
    }
  }, [searchParams, hasEditPermission, editPermissionLoading]);

  // Add explicit view mode flag
  const isViewMode = !isEditMode && hasPermission;

  const numericFileId = fileId ? parseInt(fileId, 10) : 0;
  const isValidFileId = !isNaN(numericFileId) && numericFileId > 0;

  // Get pagination parameters from URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const sortField = searchParams.get('sort') || '';
  const sortDirection = (searchParams.get('direction') || '') as 'ASC' | 'DESC';

  // Mutation hook for updating stratum
  const updateStratumMutation = useUpdateE53Stratum();

  const handleBack = () => {
    navigate(`/e53/samples/${fileId}${isEditMode ? '?editMode=true' : ''}`);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e53');
  };

  // Update URL when pagination parameters change
  const updateSearchParams = (params: Record<string, string>) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Update the search params with the new values
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });

    // Preserve edit mode parameter if it exists
    if (isEditMode) {
      newSearchParams.set('editMode', 'true');
    }

    // Update the URL without reloading the page
    setSearchParams(newSearchParams);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    updateSearchParams({ page: newPage.toString() });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    // First clear the size parameter to force a query parameter change
    const tempParams = new URLSearchParams(searchParams);
    tempParams.delete('size');
    tempParams.delete('page');
    setSearchParams(tempParams);

    // Then set it again with a slight delay to ensure URL changes are processed
    setTimeout(() => {
      updateSearchParams({
        page: '0', // Reset to first page when changing page size
        size: newSize.toString(),
      });
    }, 0);
  };

  // Handle sorting
  const handleSort = (column: string, direction: 'ASC' | 'DESC' | undefined, searchColumn?: string, searchTerm?: string) => {
    const params: Record<string, string> = {};
    
    // Only set sort params if direction is provided and not undefined
    if (direction && direction !== 'undefined' as any) {
      params.sort = column;
      params.direction = direction;
    }
    
    if (searchColumn && searchTerm) {
      params.searchColumn = searchColumn;
      params.searchTerm = searchTerm;
    }
    
    updateSearchParams(params);
  };

  // Fetch stratum data for the selected file with pagination parameters
  const {
    data: stratumResponse,
    isLoading,
    isError,
    error,
    refetch: refetchStratumData,
  } = useE53StratumData({
    fileId: numericFileId,
    page,
    size: pageSize,
    sort: sortField,
    direction: sortDirection,
    enabled: isValidFileId,
  });

  // Prepare data for the table
  const stratumData = useMemo(() => {
    return stratumResponse?.content || [];
  }, [stratumResponse]);

  // Use hook to fetch file details
  const {
    data: fileDetails,
    isLoading: isFileDetailsLoading,
    isError: isFileDetailsError,
  } = useE53FileDetails({
    fileId: numericFileId,
    enabled: isValidFileId,
  });

  // Define columns for the table
  const columns = useMemo(
    () => [
      {
        Header: 'stratum_id',
        accessor: 'stratumId',
      },
      {
        Header: 'stratum_status',
        accessor: 'stratumStatus',
        Cell: ({ value }: { value: any }) => value,
      },
      {
        Header: 'oe_from_code',
        accessor: 'oeFromCode',
      },
      {
        Header: 'oe_to_code',
        accessor: 'oeToCode',
      },
      {
        Header: 'mail_category',
        accessor: 'mailCategory',
      },
      {
        Header: 'format_corrected',
        accessor: 'formatCorrected',
      },
      {
        Header: 'net_or_gross_accounting',
        accessor: 'netOrGrossAccounting',
      },
      {
        Header: 'receptacle_category',
        accessor: 'receptacleCategory',
      },
      {
        Header: 'received_weight_stratum',
        accessor: 'receivedWeightStratum',
      },
      {
        Header: 'sampled_weight_stratum',
        accessor: 'sampledWeightStratum',
      },
      {
        Header: 'effective_sampling_rate_stratum',
        accessor: 'effectiveSamplingRateStratum',
      },
      {
        Header: 'received_receptacle_stratum',
        accessor: 'receivedReceptacleStratum',
      },
    ],
    []
  );

  // Define which columns are essential for mobile view
  const essentialColumns = [
    'stratum_id',
    'oe_from_code',
    'oe_to_code',
    'mail_category',
    'format_corrected',
    'received_weight_stratum',
  ];

  // Filter columns based on screen size
  const filteredColumns = useMemo(() => {
    // On mobile, only show essential columns
    if (isMobile) {
      return columns.filter((column) => essentialColumns.includes(column.accessor as string));
    }
    // On desktop, show all columns
    return columns;
  }, [columns, isMobile, essentialColumns]);

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Define handlers for cell editing
  const handleCellEdit = (row: any, column: string, value: any) => {
    console.log('Cell edited:', { row, column, value });
    // We'll handle this when saving
  };

  const handleRowSave = async (row: E53StratumData, originalRow: E53StratumData) => {

    try {
      // Need to include all required fields from E53StratumData type
      // Though only receivedWeightStratum and receivedReceptacleStratum are being edited
      const updateData = {
        // Required fields from row
        stratumId: row.stratumId,
        datasetId: row.datasetId || numericFileId,
        fileId: numericFileId,
        stratumStatus: row.stratumStatus,
        oeFromCode: row.oeFromCode,
        oeToCode: row.oeToCode,
        mailCategory: row.mailCategory,
        formatCorrected: row.formatCorrected,
        netOrGrossAccounting: row.netOrGrossAccounting,
        receptacleCategory: row.receptacleCategory,
        
        // Fields handled as strings now
        sampledWeightStratum: row.sampledWeightStratum,
        effectiveSamplingRateStratum: row.effectiveSamplingRateStratum,
        
        // Actually editable fields - these are the only ones that might have changed
        // Also handled as strings now
        receivedWeightStratum: row.receivedWeightStratum,
        receivedReceptacleStratum: row.receivedReceptacleStratum,
      };

      // Call the mutation to update the stratum
      await updateStratumMutation.mutateAsync(updateData);

      setNotification({
        open: true,
        message: t('e53.viewStratum.updateSuccess', 'Stratum updated successfully'),
        type: 'success',
      });

      // Refresh the data
      refetchStratumData();

      setEditingRowId(null); // Clear editing state after save
      return true; // Return true if save was successful
    } catch (error) {
      console.error('Error updating stratum row', error);
      setNotification({
        open: true,
        message: t('e53.viewStratum.updateError', 'Failed to update stratum'),
        type: 'error',
      });
      return false;
    }
  };

  const handleRowCancel = (row: any) => {
    setEditingRowId(null); // Clear editing state
  };

  // Context menu action handler
  const handleContextMenuAction = (action: ContextMenuAction, row: any) => {

    if (action === 'edit') {
      // Set this row for editing and open the edit modal
      if (isEditMode) {
        setEditingRowId(row.stratumId);
        setSelectedRow(row);
        setIsEditModalOpen(true);
      } else {
        console.log('Edit mode is not enabled. Current mode:', isEditMode);
      }
    }
  };

  // Handle closing the edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRow(null);
    setEditingRowId(null);
  };

  // Define custom context menu actions for the stratum table
  const customContextMenuActions = useMemo(() => {
    // For edit mode, return edit action
    if (isEditMode) {
      return [
        {
          action: 'edit' as ContextMenuAction,
          label: t('e53.viewStratum.contextMenu.editStratum', 'Edit Stratum'),
          icon: <EditIcon />,
          isVisible: () => true, // Always visible in edit mode
        },
      ];
    }
    // Return empty array for view mode
    else {
      return [];
    }
  }, [isEditMode, t]);

  const getTableContent = () => {
    if (!isValidFileId) {
      return (
        <Box sx={{ p: 2 }} data-testid="e53-stratum-invalid-id">
          <Typography variant='body1' color='error'>
            {t('e53.viewStratum.invalidFileId', 'Invalid file ID provided')}
          </Typography>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="e53-stratum-loading">
          <CircularProgress />
        </Box>
      );
    }

    if (isError) {
      return (
        <Box sx={{ p: 2, color: 'error.main' }} data-testid="e53-stratum-error">
          <Typography variant='body1'>
            {t('e53.viewStratum.error', 'Error loading stratum data')}
          </Typography>
          {error instanceof Error && (
            <Typography variant='body2' sx={{ mt: 1 }}>
              {error.message}
            </Typography>
          )}
        </Box>
      );
    }

    if (stratumData.length === 0) {
      return (
        <Box sx={{ p: 2 }} data-testid="e53-stratum-no-data">
          <Typography variant='body1'>
            {t('e53.viewStratum.noData', 'No stratum data available for this file')}
          </Typography>
        </Box>
      );
    }

    // Process data to add ID for proper row editing
    const dataWithIds = stratumData.map((item: any) => ({
      ...item,
      id: item.stratumId, // Ensure each row has an id property for editing
    }));

    return (
      <Box data-testid="e53-stratum-table-container">
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0 }} data-testid="e53-stratum-mobile-note">
            <Tooltip title='Swipe left and right to view all data columns'>
              <InfoIcon color='primary' sx={{ mr: 1, fontSize: 18 }} />
            </Tooltip>
            <Typography variant='caption' color='text.secondary'>
              {t(
                'e53.viewStratum.mobileNote',
                'Swipe horizontally to view all columns. Only showing essential columns on mobile.'
              )}
            </Typography>
          </Box>
        )}
        
        {isEditMode && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="e53-stratum-edit-info">
            {t(
              'e53.viewStratum.editNote',
              'Only "Received Weight Stratum" and "Received Receptacle Stratum" fields can be edited.'
            )}
          </Alert>
        )}
        
        <Table
          data={dataWithIds}
          columns={filteredColumns}
          moduleName={t(
            'e53.viewStratum.tableTitle',
            `Stratum Information${isEditMode ? ' (Edit Mode)' : ''}`
          )}
          totalCount={stratumResponse?.totalElements}
          pageSize={pageSize}
          page={page}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
          onSort={handleSort}
          defaultSort={{ column: sortField, direction: sortDirection }}
          disableAutoSizing={false}
          viewMode={!isEditMode}
          editMode={isEditMode}
          onCellEdit={isEditMode ? handleCellEdit : undefined}
          onRowSave={isEditMode ? handleRowSave : undefined}
          onRowCancel={isEditMode ? handleRowCancel : undefined}
          isRowEditable={(row: E53StratumData) => isEditMode && row.stratumStatus !== 'FINALIZED'}
          readOnlyColumns={[
            'stratumId',
            'stratumStatus',
            'oeFromCode',
            'oeToCode',
            'mailCategory',
            'formatCorrected',
            'netOrGrossAccounting',
            'receptacleCategory',
            'sampledWeightStratum',
            'effectiveSamplingRateStratum',
          ]}
          rowEventHandlers={{
            onContextMenuAction: handleContextMenuAction,
          }}
          customActions={customContextMenuActions}
          hideDefaultActions={false}
          data-testid="e53-stratum-table"
        />
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="e53-stratum-container">
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title={`e53 Stratum - File ID: ${fileId}${isEditMode ? ' (Edit Mode)' : ''}`}
            subtitle={`e53 Sample - File ID: ${fileId}${isEditMode ? ' (Edit Mode)' : ''}`}
            onToggleSidebar={handleSidebarToggle}
            onSubtitleClick={handleBack}
            isSidebarOpen={sidebarOpen}
            showFilters={{}}
            data-testid="e53-stratum-section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid="e53-stratum-sidebar-container">
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="e53-stratum-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid="e53-stratum-main-content">
            {permissionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="e53-stratum-permission-loading">
                <CircularProgress />
              </Box>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid="e53-stratum-permission-alert">You don't have permission to view this section</Alert>
            ) : isEditMode && !hasEditPermission ? (
              <Alert severity='warning' data-testid="e53-stratum-edit-permission-alert">You don't have permission to edit stratum data</Alert>
            ) : (
              <StyledContainer data-testid="e53-stratum-content-container">
                <ContentPaper data-testid="e53-stratum-content-paper">{getTableContent()}</ContentPaper>
              </StyledContainer>
            )}
          </MainContent>
        </Grid>

        {/* Notification Snackbar */}
        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.type}
          onClose={handleNotificationClose}
          data-testid="e53-stratum-notification"
        />

        {/* EditModal for stratum editing */}
        {isEditMode && (
          <EditModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            onSave={handleRowSave}
            row={selectedRow}
            columns={columns}
            excludedColumns={[]}
            readOnlyColumns={[
              'stratumId',
              'stratumStatus',
              'oeFromCode',
              'oeToCode',
              'mailCategory',
              'formatCorrected',
              'netOrGrossAccounting',
              'receptacleCategory',
              'sampledWeightStratum',
              'effectiveSamplingRateStratum',
            ]}
            title={t('e53.viewStratum.editStratum', 'Edit Stratum')}
            data-testid="e53-stratum-edit-modal"
          />
        )}
      </Grid>
    </Box>
  );
};

export default ViewStratum;
